import { createTamagui } from 'tamagui'
import { defaultConfig } from '@tamagui/config/v4'

const spaceGroteskFont = {
  family: 'Space Grotesk',
  face: {
    300: { normal: 'Space Grotesk' },
    400: { normal: 'Space Grotesk' },
    500: { normal: 'Space Grotesk' },
    600: { normal: 'Space Grotesk' },
    700: { normal: 'Space Grotesk' },
    800: { normal: 'Space Grotesk' },
    900: { normal: 'Space Grotesk' },
  },
  size: {
    1: 11, 2: 12, 3: 13, 4: 14, 5: 16, 6: 18, 7: 20, 8: 24,
    9: 32, 10: 48, 11: 56, 12: 64, 13: 80, 14: 96, 15: 128, true: 16,
  },
  lineHeight: {
    1: 14, 2: 16, 3: 18, 4: 20, 5: 22, 6: 26, 7: 28, 8: 32,
    9: 40, 10: 56, 11: 64, 12: 72, 13: 88, 14: 104, 15: 140, true: 22,
  },
  weight: {
    1: '300', 2: '400', 3: '500', 4: '600', 5: '700', 6: '800', 7: '900',
  },
  letterSpacing: {
    1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: -0.5, 7: -0.5, 8: -0.5,
    9: -1, 10: -1.5, 11: -2, 12: -2, 13: -2.5, 14: -3, 15: -3, true: 0,
  },
}

// ─── Torch (Default) ─────────────────────────────────────────
// Warm dark + flame accent. Ember glow aesthetic.

const torchDark = {
  background: '#0a0908',
  backgroundHover: '#100f0d',
  backgroundPress: '#0c0b0a',
  backgroundFocus: '#100f0d',
  backgroundStrong: '#070605',
  backgroundTransparent: 'rgba(10, 9, 8, 0)',

  color: '#fafafa',
  colorHover: '#ffffff',
  colorPress: '#d4d4d8',
  colorFocus: '#ffffff',
  colorTransparent: 'rgba(250, 250, 250, 0)',

  color1: '#0a0908', color2: '#121110', color3: '#1a1918', color4: '#282624',
  color5: '#3e3c38', color6: '#52525b', color7: '#71717a', color8: '#a1a1aa',
  color9: '#d4d4d8', color10: '#e4e4e7', color11: '#f4f4f5', color12: '#fafafa',

  accentBackground: '#d4944a',
  accentColor: '#09090b',

  borderColor: '#3e3c38',
  borderColorHover: '#4a4844',
  borderColorPress: '#3e3c38',
  borderColorFocus: '#d4944a',

  shadowColor: 'rgba(0, 0, 0, 0.5)',
  shadowColorHover: 'rgba(0, 0, 0, 0.6)',

  blue1: '#0c1929', blue2: '#0f2942', blue3: '#133a5c', blue4: '#174b77',
  blue5: '#1b5c93', blue6: '#1f6eaf', blue7: '#3b82f6', blue8: '#60a5fa',
  blue9: '#3b82f6', blue10: '#60a5fa', blue11: '#93c5fd', blue12: '#dbeafe',

  purple1: '#1a0f2e', purple2: '#2d1a4d', purple3: '#41256d', purple4: '#56318e',
  purple5: '#6b3daf', purple6: '#8149d0', purple7: '#8b5cf6', purple8: '#a78bfa',
  purple9: '#8b5cf6', purple10: '#a78bfa', purple11: '#c4b5fd', purple12: '#ede9fe',

  green1: '#052e16', green2: '#0a3d1f', green3: '#0f4d28', green4: '#155e31',
  green5: '#1a6f3a', green6: '#1f8044', green7: '#22c55e', green8: '#4ade80',
  green9: '#22c55e', green10: '#4ade80', green11: '#86efac', green12: '#dcfce7',

  red1: '#2c0f0f', red2: '#451a1a', red3: '#5e2525', red4: '#783030',
  red5: '#923b3b', red6: '#ac4747', red7: '#ef4444', red8: '#f87171',
  red9: '#ef4444', red10: '#f87171', red11: '#fca5a5', red12: '#fee2e2',

  yellow1: '#2e2a05', yellow2: '#453f0a', yellow3: '#5c530f', yellow4: '#736815',
  yellow5: '#8a7d1a', yellow6: '#a1921f', yellow7: '#eab308', yellow8: '#facc15',
  yellow9: '#eab308', yellow10: '#facc15', yellow11: '#fde047', yellow12: '#fef9c3',

  // Amber mapped to flame accent
  amber1: '#1c1408', amber2: '#2a2010', amber3: '#382c18', amber4: '#463820',
  amber5: '#544428', amber6: '#6a5634', amber7: '#d4944a', amber8: '#e0a860',
  amber9: '#d4944a', amber10: '#e0a860', amber11: '#ecbc78', amber12: '#f4d8a8',
}

const torchLight = {
  background: '#ffffff',
  backgroundHover: '#fafafa',
  backgroundPress: '#f5f5f5',
  backgroundFocus: '#fafafa',
  backgroundStrong: '#f0f0f0',
  backgroundTransparent: 'rgba(255, 255, 255, 0)',

  color: '#0a0a0b',
  colorHover: '#000000',
  colorPress: '#171717',
  colorFocus: '#000000',
  colorTransparent: 'rgba(10, 10, 11, 0)',

  color1: '#ffffff', color2: '#fafafa', color3: '#f5f5f5', color4: '#e5e5e5',
  color5: '#d4d4d4', color6: '#a3a3a3', color7: '#737373', color8: '#525252',
  color9: '#404040', color10: '#262626', color11: '#171717', color12: '#0a0a0b',

  accentBackground: '#ededed',
  accentColor: '#000000',

  borderColor: '#e5e5e5',
  borderColorHover: '#d4d4d4',
  borderColorPress: '#e5e5e5',
  borderColorFocus: '#ededed',

  shadowColor: 'rgba(0, 0, 0, 0.1)',
  shadowColorHover: 'rgba(0, 0, 0, 0.15)',

  blue1: '#eff6ff', blue2: '#dbeafe', blue3: '#bfdbfe', blue4: '#93c5fd',
  blue5: '#60a5fa', blue6: '#3b82f6', blue7: '#2563eb', blue8: '#1d4ed8',
  blue9: '#3b82f6', blue10: '#2563eb', blue11: '#1d4ed8', blue12: '#1e3a8a',

  purple1: '#faf5ff', purple2: '#f3e8ff', purple3: '#e9d5ff', purple4: '#d8b4fe',
  purple5: '#c084fc', purple6: '#a855f7', purple7: '#9333ea', purple8: '#7c3aed',
  purple9: '#8b5cf6', purple10: '#7c3aed', purple11: '#6d28d9', purple12: '#4c1d95',

  green1: '#f0fdf4', green2: '#dcfce7', green3: '#bbf7d0', green4: '#86efac',
  green5: '#4ade80', green6: '#22c55e', green7: '#16a34a', green8: '#15803d',
  green9: '#22c55e', green10: '#16a34a', green11: '#15803d', green12: '#14532d',

  red1: '#fef2f2', red2: '#fee2e2', red3: '#fecaca', red4: '#fca5a5',
  red5: '#f87171', red6: '#ef4444', red7: '#dc2626', red8: '#b91c1c',
  red9: '#ef4444', red10: '#dc2626', red11: '#b91c1c', red12: '#7f1d1d',

  yellow1: '#fefce8', yellow2: '#fef9c3', yellow3: '#fef08a', yellow4: '#fde047',
  yellow5: '#facc15', yellow6: '#eab308', yellow7: '#ca8a04', yellow8: '#a16207',
  yellow9: '#eab308', yellow10: '#ca8a04', yellow11: '#a16207', yellow12: '#713f12',

  amber1: '#fdf8f0', amber2: '#faf0dc', amber3: '#f4e0c0', amber4: '#ecd0a0',
  amber5: '#e0b880', amber6: '#d4a060', amber7: '#b07830', amber8: '#8c5c20',
  amber9: '#b07830', amber10: '#8c5c20', amber11: '#6c4418', amber12: '#4c2e10',
}

// ─── Odyssey ─────────────────────────────────────────────────
// Aegean depths, golden eagle, Mediterranean stone

const odysseyDark = {
  background: '#080c12',
  backgroundHover: '#0c1018',
  backgroundPress: '#0a0e14',
  backgroundFocus: '#0c1018',
  backgroundStrong: '#05080c',
  backgroundTransparent: 'rgba(8, 12, 18, 0)',

  color: '#e8e4d8', colorHover: '#f4f0e4', colorPress: '#c4bea8',
  colorFocus: '#f4f0e4', colorTransparent: 'rgba(232, 228, 216, 0)',

  color1: '#080c12', color2: '#0e1420', color3: '#161e2a', color4: '#1e2836',
  color5: '#283244', color6: '#445060', color7: '#647080', color8: '#8894a4',
  color9: '#a8b4c0', color10: '#c4ccd4', color11: '#dce0e4', color12: '#e8e4d8',

  accentBackground: '#c8a84e', accentColor: '#080c12',

  borderColor: '#283244', borderColorHover: '#344054',
  borderColorPress: '#1e2836', borderColorFocus: '#c8a84e',

  shadowColor: 'rgba(4, 8, 16, 0.8)', shadowColorHover: 'rgba(4, 8, 16, 0.9)',

  blue1: '#060a14', blue2: '#0c1428', blue3: '#121e3c', blue4: '#182850',
  blue5: '#1e3464', blue6: '#284478', blue7: '#3a6ca8', blue8: '#5088c0',
  blue9: '#3a6ca8', blue10: '#5088c0', blue11: '#78a8d8', blue12: '#b0d0f0',

  purple1: '#0c0814', purple2: '#161028', purple3: '#20183c', purple4: '#2c2050',
  purple5: '#382864', purple6: '#483478', purple7: '#6850a0', purple8: '#8068b8',
  purple9: '#6850a0', purple10: '#8068b8', purple11: '#a08cd0', purple12: '#c8b8e8',

  green1: '#081008', green2: '#0c1c0e', green3: '#142814', green4: '#1c341c',
  green5: '#244024', green6: '#2c502c', green7: '#4c8c3c', green8: '#68a854',
  green9: '#4c8c3c', green10: '#68a854', green11: '#90c478', green12: '#c0e4ac',

  red1: '#140808', red2: '#240e0c', red3: '#381414', red4: '#4c1a1a',
  red5: '#602020', red6: '#742828', red7: '#b83030', red8: '#d04848',
  red9: '#b83030', red10: '#d04848', red11: '#e07468', red12: '#f0b4a8',

  yellow1: '#141008', yellow2: '#221c0c', yellow3: '#322810', yellow4: '#443614',
  yellow5: '#56441a', yellow6: '#685420', yellow7: '#c8a84e', yellow8: '#d8bc68',
  yellow9: '#c8a84e', yellow10: '#d8bc68', yellow11: '#e8d48c', yellow12: '#f4ecbc',

  amber1: '#12100a', amber2: '#1e1a10', amber3: '#2c2618', amber4: '#3c3420',
  amber5: '#4c4228', amber6: '#605430', amber7: '#c8a84e', amber8: '#d8bc68',
  amber9: '#c8a84e', amber10: '#d8bc68', amber11: '#e8d48c', amber12: '#f4ecbc',
}

// ─── Rome ────────────────────────────────────────────────────
const romeDark = {
  background: '#0c0a07', backgroundHover: '#12100b', backgroundPress: '#0e0c09',
  backgroundFocus: '#12100b', backgroundStrong: '#070604',
  backgroundTransparent: 'rgba(12, 10, 7, 0)',

  color: '#f0ebe0', colorHover: '#f8f4ec', colorPress: '#ccc4b4',
  colorFocus: '#f8f4ec', colorTransparent: 'rgba(240, 235, 224, 0)',

  color1: '#0c0a07', color2: '#15120c', color3: '#201c14', color4: '#2a261c',
  color5: '#363024', color6: '#5c5240', color7: '#7c6e58', color8: '#a09478',
  color9: '#c0b49c', color10: '#d8ccb4', color11: '#ece4d4', color12: '#f0ebe0',

  accentBackground: '#c41e1e', accentColor: '#f0ebe0',

  borderColor: '#363024', borderColorHover: '#443c2c',
  borderColorPress: '#2a261c', borderColorFocus: '#c41e1e',

  shadowColor: 'rgba(12, 8, 4, 0.7)', shadowColorHover: 'rgba(12, 8, 4, 0.85)',

  blue1: '#0a0c10', blue2: '#10141a', blue3: '#161c26', blue4: '#1c2432',
  blue5: '#242e40', blue6: '#344050', blue7: '#5c6e82', blue8: '#788a9c',
  blue9: '#5c6e82', blue10: '#788a9c', blue11: '#98a8b6', blue12: '#c4d0d8',

  purple1: '#100810', purple2: '#1c1020', purple3: '#281830', purple4: '#342040',
  purple5: '#402850', purple6: '#503464', purple7: '#7c4d8a', purple8: '#9668a4',
  purple9: '#7c4d8a', purple10: '#9668a4', purple11: '#b48cc0', purple12: '#d8c0e0',

  green1: '#0c100a', green2: '#141c10', green3: '#1c2818', green4: '#243420',
  green5: '#2c4028', green6: '#384c30', green7: '#5c9040', green8: '#78ac58',
  green9: '#5c9040', green10: '#78ac58', green11: '#9cc47c', green12: '#cce4b0',

  red1: '#180808', red2: '#280e0e', red3: '#3c1414', red4: '#501a1a',
  red5: '#642020', red6: '#7c2828', red7: '#c41e1e', red8: '#d84040',
  red9: '#c41e1e', red10: '#d84040', red11: '#e47060', red12: '#f0b0a0',

  yellow1: '#141008', yellow2: '#221a0c', yellow3: '#302610', yellow4: '#403214',
  yellow5: '#504018', yellow6: '#644e20', yellow7: '#d4a020', yellow8: '#e8b840',
  yellow9: '#d4a020', yellow10: '#e8b840', yellow11: '#f0d068', yellow12: '#f8e8a0',

  amber1: '#140e08', amber2: '#201a0e', amber3: '#2e2414', amber4: '#3c301c',
  amber5: '#4c3c24', amber6: '#604c30', amber7: '#d4a020', amber8: '#e8b840',
  amber9: '#d4a020', amber10: '#e8b840', amber11: '#f0d068', amber12: '#f8e8a0',
}

// ─── SpaceX ──────────────────────────────────────────────────
const spacexDark = {
  background: '#000000', backgroundHover: '#080808', backgroundPress: '#040404',
  backgroundFocus: '#080808', backgroundStrong: '#000000',
  backgroundTransparent: 'rgba(0, 0, 0, 0)',

  color: '#f0f0fa', colorHover: '#ffffff', colorPress: '#b0b0c0',
  colorFocus: '#ffffff', colorTransparent: 'rgba(240, 240, 250, 0)',

  color1: '#000000', color2: '#08080a', color3: '#101012', color4: '#1a1a1e',
  color5: '#24242a', color6: '#3c3c46', color7: '#5c5c6a', color8: '#888896',
  color9: '#aaaab6', color10: '#c8c8d2', color11: '#e0e0e8', color12: '#f0f0fa',

  accentBackground: '#f0f0fa', accentColor: '#000000',

  borderColor: '#24242a', borderColorHover: '#303038',
  borderColorPress: '#1a1a1e', borderColorFocus: '#f0f0fa',

  shadowColor: 'rgba(0, 0, 0, 0.9)', shadowColorHover: 'rgba(0, 0, 0, 0.95)',

  blue1: '#000008', blue2: '#00000f', blue3: '#000820', blue4: '#001030',
  blue5: '#001840', blue6: '#002460', blue7: '#3060c0', blue8: '#4878d8',
  blue9: '#3060c0', blue10: '#4878d8', blue11: '#7098e8', blue12: '#b0c8f4',

  purple1: '#040008', purple2: '#08000f', purple3: '#100020', purple4: '#180030',
  purple5: '#200040', purple6: '#300060', purple7: '#6040c0', purple8: '#7858d8',
  purple9: '#6040c0', purple10: '#7858d8', purple11: '#9878e8', purple12: '#c4b0f4',

  green1: '#000800', green2: '#001000', green3: '#002000', green4: '#003000',
  green5: '#004000', green6: '#006000', green7: '#20a860', green8: '#40c878',
  green9: '#20a860', green10: '#40c878', green11: '#70e0a0', green12: '#b0f0c8',

  red1: '#080000', red2: '#100000', red3: '#200000', red4: '#300000',
  red5: '#400808', red6: '#501010', red7: '#d03030', red8: '#e05050',
  red9: '#d03030', red10: '#e05050', red11: '#f08080', red12: '#f8b8b8',

  yellow1: '#080800', yellow2: '#101000', yellow3: '#202000', yellow4: '#303000',
  yellow5: '#404000', yellow6: '#505010', yellow7: '#c8b020', yellow8: '#e0c840',
  yellow9: '#c8b020', yellow10: '#e0c840', yellow11: '#f0e070', yellow12: '#f8f0b0',

  amber1: '#000000', amber2: '#08080a', amber3: '#101012', amber4: '#1a1a1e',
  amber5: '#24242a', amber6: '#3c3c46', amber7: '#f0f0fa', amber8: '#e0e0e8',
  amber9: '#f0f0fa', amber10: '#e0e0e8', amber11: '#c8c8d2', amber12: '#aaaab6',
}

// ─── Coinbase ────────────────────────────────────────────────
const coinbaseDark = {
  background: '#0a0b0d', backgroundHover: '#0f1012', backgroundPress: '#0c0d0f',
  backgroundFocus: '#0f1012', backgroundStrong: '#060608',
  backgroundTransparent: 'rgba(10, 11, 13, 0)',

  color: '#f5f5f7', colorHover: '#ffffff', colorPress: '#c0c0c8',
  colorFocus: '#ffffff', colorTransparent: 'rgba(245, 245, 247, 0)',

  color1: '#0a0b0d', color2: '#111214', color3: '#1a1b1e', color4: '#232428',
  color5: '#2c2d32', color6: '#4a4b52', color7: '#6c6d76', color8: '#92939c',
  color9: '#b4b5bc', color10: '#d0d1d6', color11: '#e8e8ec', color12: '#f5f5f7',

  accentBackground: '#0052ff', accentColor: '#ffffff',

  borderColor: '#2c2d32', borderColorHover: '#38393e',
  borderColorPress: '#232428', borderColorFocus: '#0052ff',

  shadowColor: 'rgba(0, 0, 0, 0.7)', shadowColorHover: 'rgba(0, 0, 0, 0.85)',

  blue1: '#000818', blue2: '#001030', blue3: '#001848', blue4: '#002060',
  blue5: '#002878', blue6: '#003890', blue7: '#0052ff', blue8: '#3374ff',
  blue9: '#0052ff', blue10: '#3374ff', blue11: '#6698ff', blue12: '#b0ccff',

  purple1: '#040010', purple2: '#080020', purple3: '#100030', purple4: '#180040',
  purple5: '#200050', purple6: '#300068', purple7: '#5040d0', purple8: '#6858e0',
  purple9: '#5040d0', purple10: '#6858e0', purple11: '#8878f0', purple12: '#c0b4f8',

  green1: '#000a04', green2: '#001408', green3: '#002010', green4: '#003018',
  green5: '#004020', green6: '#005028', green7: '#00b858', green8: '#20d470',
  green9: '#00b858', green10: '#20d470', green11: '#60e898', green12: '#b0f4c8',

  red1: '#0d0404', red2: '#1a0808', red3: '#2a0e0e', red4: '#3a1414',
  red5: '#4a1a1a', red6: '#5c2020', red7: '#e04040', red8: '#e86060',
  red9: '#e04040', red10: '#e86060', red11: '#f09090', red12: '#f8c4c4',

  yellow1: '#0a0800', yellow2: '#141000', yellow3: '#201a00', yellow4: '#2c2400',
  yellow5: '#382e00', yellow6: '#4a3c00', yellow7: '#d0a800', yellow8: '#e8c020',
  yellow9: '#d0a800', yellow10: '#e8c020', yellow11: '#f0d860', yellow12: '#f8ecb0',

  amber1: '#000818', amber2: '#001030', amber3: '#001848', amber4: '#002060',
  amber5: '#002878', amber6: '#003890', amber7: '#0052ff', amber8: '#3374ff',
  amber9: '#0052ff', amber10: '#3374ff', amber11: '#6698ff', amber12: '#b0ccff',
}

// ─── God of War ──────────────────────────────────────────────
// Norse frost + runic gold on dark slate. Cold precision, mythic authority.

const gowDark = {
  background: '#0a0c10',
  backgroundHover: '#0e1016',
  backgroundPress: '#0c0e12',
  backgroundFocus: '#0e1016',
  backgroundStrong: '#06080c',
  backgroundTransparent: 'rgba(10, 12, 16, 0)',

  color: '#e4e8f0',
  colorHover: '#f0f2f8',
  colorPress: '#b8bcc8',
  colorFocus: '#f0f2f8',
  colorTransparent: 'rgba(228, 232, 240, 0)',

  // Cool slate neutrals — Midgard stone
  color1: '#0a0c10', color2: '#101418', color3: '#181c22', color4: '#20262e',
  color5: '#2a323c', color6: '#404854', color7: '#5c6878', color8: '#808c9c',
  color9: '#a0acb8', color10: '#bcc4d0', color11: '#d4dae4', color12: '#e4e8f0',

  // Runic gold — Leviathan Axe frost meets Blades of Chaos fire
  accentBackground: '#c8a040',
  accentColor: '#0a0c10',

  borderColor: '#2a323c',
  borderColorHover: '#364050',
  borderColorPress: '#2a323c',
  borderColorFocus: '#c8a040',

  shadowColor: 'rgba(6, 8, 14, 0.8)',
  shadowColorHover: 'rgba(6, 8, 14, 0.9)',

  // Blue — Leviathan Axe frost
  blue1: '#060a14', blue2: '#0c1428', blue3: '#101e3c', blue4: '#142850',
  blue5: '#1a3468', blue6: '#204480', blue7: '#3070c0', blue8: '#4890d8',
  blue9: '#3070c0', blue10: '#4890d8', blue11: '#70b0e8', blue12: '#a8d4f4',

  // Purple — Realm travel, Bifrost energy
  purple1: '#0c0814', purple2: '#140e24', purple3: '#1c1434', purple4: '#261c48',
  purple5: '#30245c', purple6: '#3c3074', purple7: '#5c48a8', purple8: '#7460c0',
  purple9: '#5c48a8', purple10: '#7460c0', purple11: '#9880d8', purple12: '#c4b0f0',

  // Green — Yggdrasil, world tree
  green1: '#060e08', green2: '#0c1c10', green3: '#142818', green4: '#1c3420',
  green5: '#244028', green6: '#304c30', green7: '#3c8c44', green8: '#54a85c',
  green9: '#3c8c44', green10: '#54a85c', green11: '#7cc480', green12: '#b8e4b8',

  // Red — Blades of Chaos, Spartan rage
  red1: '#140608', red2: '#240c10', red3: '#381218', red4: '#4c1820',
  red5: '#601e28', red6: '#782630', red7: '#c03040', red8: '#d84858',
  red9: '#c03040', red10: '#d84858', red11: '#e87478', red12: '#f4b0b0',

  yellow1: '#141008', yellow2: '#201c0c', yellow3: '#302810', yellow4: '#403414',
  yellow5: '#504018', yellow6: '#645020', yellow7: '#c8a040', yellow8: '#d8b858',
  yellow9: '#c8a040', yellow10: '#d8b858', yellow11: '#e8d080', yellow12: '#f4e8b0',

  // Amber — runic gold scale
  amber1: '#100e08', amber2: '#1c1a10', amber3: '#2a2618', amber4: '#383220',
  amber5: '#463e28', amber6: '#5c5034', amber7: '#c8a040', amber8: '#d8b858',
  amber9: '#c8a040', amber10: '#d8b858', amber11: '#e8d080', amber12: '#f4e8b0',
}

// ─── Hades ───────────────────────────────────────────────────
// Greek underworld: blood-orange + gold on deep warm blacks.
// Everything lit by hellfire. Rich, saturated, unapologetically bold.

const hadesDark = {
  background: '#0c0806',
  backgroundHover: '#121010',
  backgroundPress: '#0e0a08',
  backgroundFocus: '#121010',
  backgroundStrong: '#080604',
  backgroundTransparent: 'rgba(12, 8, 6, 0)',

  color: '#f0e8dc',
  colorHover: '#f8f2e8',
  colorPress: '#c8bca8',
  colorFocus: '#f8f2e8',
  colorTransparent: 'rgba(240, 232, 220, 0)',

  // Warm ashen neutrals — underworld stone
  color1: '#0c0806', color2: '#14100c', color3: '#1e1814', color4: '#28201a',
  color5: '#342a22', color6: '#544840', color7: '#786858', color8: '#9c8c78',
  color9: '#c0b09c', color10: '#d4c8b4', color11: '#e8dece', color12: '#f0e8dc',

  // Blood-orange — the river Styx, Zagreus' essence
  accentBackground: '#d45030',
  accentColor: '#f0e8dc',

  borderColor: '#342a22',
  borderColorHover: '#40342c',
  borderColorPress: '#342a22',
  borderColorFocus: '#d45030',

  shadowColor: 'rgba(12, 6, 4, 0.8)',
  shadowColorHover: 'rgba(12, 6, 4, 0.9)',

  // Blue — rare, cold contrast (Poseidon boons)
  blue1: '#080a14', blue2: '#0e1424', blue3: '#141e34', blue4: '#1a2844',
  blue5: '#203458', blue6: '#28446c', blue7: '#3868a8', blue8: '#5084c0',
  blue9: '#3868a8', blue10: '#5084c0', blue11: '#78a4d8', blue12: '#b0ccf0',

  // Purple — Chaos, the void, Nyx
  purple1: '#100810', purple2: '#1c0e1c', purple3: '#28142a', purple4: '#341c38',
  purple5: '#402448', purple6: '#503058', purple7: '#8040a0', purple8: '#9858b8',
  purple9: '#8040a0', purple10: '#9858b8', purple11: '#b880d0', purple12: '#d8b4e8',

  // Green — Demeter boons, life amidst death
  green1: '#0a0e06', green2: '#101c0c', green3: '#182812', green4: '#203418',
  green5: '#28401e', green6: '#344c28', green7: '#508830', green8: '#6ca448',
  green9: '#508830', green10: '#6ca448', green11: '#94c068', green12: '#c4e0a0',

  // Red — blood, hellfire, the dominant color of Hades
  red1: '#180606', red2: '#2c0c0a', red3: '#401210', red4: '#541816',
  red5: '#681e1c', red6: '#802824', red7: '#d45030', red8: '#e06848',
  red9: '#d45030', red10: '#e06848', red11: '#ec9070', red12: '#f4c0a8',

  // Yellow — Olympian gold, boon rewards
  yellow1: '#141008', yellow2: '#221c0c', yellow3: '#322810', yellow4: '#443414',
  yellow5: '#564018', yellow6: '#6c5020', yellow7: '#d4a028', yellow8: '#e4b840',
  yellow9: '#d4a028', yellow10: '#e4b840', yellow11: '#f0d068', yellow12: '#f8e8a0',

  // Amber — hellfire glow scale
  amber1: '#140a06', amber2: '#22140c', amber3: '#301e12', amber4: '#402818',
  amber5: '#50321e', amber6: '#644028', amber7: '#d45030', amber8: '#e06848',
  amber9: '#d45030', amber10: '#e06848', amber11: '#ec9070', amber12: '#f4c0a8',
}

// ─── Diablo IV ───────────────────────────────────────────────
// Hellfire amber on charred black. Embers in darkness.
// The closest game palette to Torch's flame identity.

const diabloDark = {
  // Cool dark canvas — warmth comes from content, not background
  background: '#0a0a0c',
  backgroundHover: '#0f0f12',
  backgroundPress: '#0c0c0e',
  backgroundFocus: '#0f0f12',
  backgroundStrong: '#060608',
  backgroundTransparent: 'rgba(10, 10, 12, 0)',

  // Text stays warm — parchment in firelight
  color: '#ece0cc',
  colorHover: '#f4ecd8',
  colorPress: '#c4b498',
  colorFocus: '#f4ecd8',
  colorTransparent: 'rgba(236, 224, 204, 0)',

  // Cool darks (1-3), warm transition (4-5), ember warmth (6-12)
  color1: '#0a0a0c', color2: '#111114', color3: '#1a1a1e', color4: '#262218',
  color5: '#322a20', color6: '#584838', color7: '#7c6850', color8: '#a08c70',
  color9: '#c0ac90', color10: '#d4c4a8', color11: '#e4d8c0', color12: '#ece0cc',

  // Hellfire amber — the eternal flame
  accentBackground: '#d48c28',
  accentColor: '#0a0a0c',

  borderColor: '#2a2620',
  borderColorHover: '#363028',
  borderColorPress: '#2a2620',
  borderColorFocus: '#d48c28',

  shadowColor: 'rgba(8, 8, 12, 0.8)',
  shadowColorHover: 'rgba(8, 8, 12, 0.9)',

  // Blue — rare, cold (Frost skill tree)
  blue1: '#080810', blue2: '#0c0e1c', blue3: '#101428', blue4: '#141a34',
  blue5: '#182044', blue6: '#202c58', blue7: '#3058a0', blue8: '#4874b8',
  blue9: '#3058a0', blue10: '#4874b8', blue11: '#70a0d0', blue12: '#a8c8e8',

  // Purple — arcane, demonic
  purple1: '#100810', purple2: '#1c0e1c', purple3: '#28142a', purple4: '#341c38',
  purple5: '#402448', purple6: '#503058', purple7: '#7840a0', purple8: '#9058b8',
  purple9: '#7840a0', purple10: '#9058b8', purple11: '#b080d0', purple12: '#d0b0e8',

  // Green — poison, plague (Necromancer)
  green1: '#080c04', green2: '#101808', green3: '#18240c', green4: '#203010',
  green5: '#283c14', green6: '#344c1c', green7: '#588c28', green8: '#70a840',
  green9: '#588c28', green10: '#70a840', green11: '#98c468', green12: '#c4e0a0',

  // Red — blood, damnation, hellfire
  red1: '#140604', red2: '#240c08', red3: '#38120c', red4: '#4c1810',
  red5: '#602014', red6: '#78281a', red7: '#c43820', red8: '#d85038',
  red9: '#c43820', red10: '#d85038', red11: '#e88060', red12: '#f4b8a0',

  // Yellow — gold drops, legendary items
  yellow1: '#141008', yellow2: '#221c0c', yellow3: '#322810', yellow4: '#443414',
  yellow5: '#564018', yellow6: '#6c5020', yellow7: '#d48c28', yellow8: '#e4a440',
  yellow9: '#d48c28', yellow10: '#e4a440', yellow11: '#f0c068', yellow12: '#f8dca0',

  // Amber — ember/flame scale (matches accent)
  amber1: '#120a04', amber2: '#1e1408', amber3: '#2c1e0e', amber4: '#3c2814',
  amber5: '#4c341a', amber6: '#604420', amber7: '#d48c28', amber8: '#e4a440',
  amber9: '#d48c28', amber10: '#e4a440', amber11: '#f0c068', amber12: '#f8dca0',
}

// ─── Campfire ────────────────────────────────────────────────
// The fire has burned down. Deep charcoal, ash, soot.
// The embers glow deep orange when you poke at them.
// Intimate, warm, quiet. Not a bonfire — the memory of one.

const campfireDark = {
  // Deep charcoal — burned-out wood, not pure black
  background: '#0c0b0a',
  backgroundHover: '#11100e',
  backgroundPress: '#0e0d0c',
  backgroundFocus: '#11100e',
  backgroundStrong: '#080706',
  backgroundTransparent: 'rgba(12, 11, 10, 0)',

  // Warm smoke-white — not bright, not clean
  color: '#e0dcd4',
  colorHover: '#ece8e0',
  colorPress: '#b8b4a8',
  colorFocus: '#ece8e0',
  colorTransparent: 'rgba(224, 220, 212, 0)',

  // Ash and soot — warm grays with a slight brown tint
  color1: '#0c0b0a', color2: '#131210', color3: '#1c1a18', color4: '#252320',
  color5: '#302d28', color6: '#4c4840', color7: '#686058', color8: '#8c8478',
  color9: '#aca498', color10: '#c4bcb0', color11: '#d8d2c8', color12: '#e0dcd4',

  // Ember glow — deep muted orange, barely alive
  accentBackground: '#c06030',
  accentColor: '#e0dcd4',

  // Borders — charcoal, barely visible
  borderColor: '#302d28',
  borderColorHover: '#3c3830',
  borderColorPress: '#302d28',
  borderColorFocus: '#c06030',

  shadowColor: 'rgba(10, 8, 6, 0.7)',
  shadowColorHover: 'rgba(10, 8, 6, 0.85)',

  // Blue — cold night sky beyond the campfire
  blue1: '#08090c', blue2: '#0c1018', blue3: '#101824', blue4: '#142030',
  blue5: '#182840', blue6: '#203450', blue7: '#3c6090', blue8: '#5478a8',
  blue9: '#3c6090', blue10: '#5478a8', blue11: '#7898c0', blue12: '#a8c0d8',

  // Purple — dusk, the sky before the fire was lit
  purple1: '#0c090c', purple2: '#141018', purple3: '#1c1824', purple4: '#242030',
  purple5: '#2c2840', purple6: '#383450', purple7: '#5c4880', purple8: '#746098',
  purple9: '#5c4880', purple10: '#746098', purple11: '#9480b4', purple12: '#bcb0d0',

  // Green — pine needles, forest floor
  green1: '#080a08', green2: '#0e140e', green3: '#142014', green4: '#1a2c1a',
  green5: '#203820', green6: '#2c4428', green7: '#447838', green8: '#5c9450',
  green9: '#447838', green10: '#5c9450', green11: '#80b470', green12: '#b0d4a0',

  // Red — the deepest ember glow
  red1: '#120808', red2: '#1e0e0c', red3: '#2c1410', red4: '#3a1a14',
  red5: '#482018', red6: '#58281e', red7: '#c06030', red8: '#d07848',
  red9: '#c06030', red10: '#d07848', red11: '#e09868', red12: '#f0c4a0',

  // Yellow — sparks that float upward
  yellow1: '#120e08', yellow2: '#1e180c', yellow3: '#2c2210', yellow4: '#3a2c14',
  yellow5: '#483618', yellow6: '#58441e', yellow7: '#b88828', yellow8: '#cca040',
  yellow9: '#b88828', yellow10: '#cca040', yellow11: '#e0bc64', yellow12: '#f0d898',

  // Amber — ember scale
  amber1: '#100a08', amber2: '#1c140c', amber3: '#281e12', amber4: '#342818',
  amber5: '#40301e', amber6: '#503c28', amber7: '#c06030', amber8: '#d07848',
  amber9: '#c06030', amber10: '#d07848', amber11: '#e09868', amber12: '#f0c4a0',
}

// ─── Radius ──────────────────────────────────────────────────

const radius = {
  0: 0, 1: 2, 2: 4, 3: 6, 4: 8, 5: 10, 6: 12,
  7: 14, 8: 16, 9: 20, 10: 24, 11: 28, 12: 32, true: 6,
}

// ─── Theme Style Registry ────────────────────────────────────

export const THEME_STYLES = {
  torch: { label: 'Torch', accent: '#d4944a' },
  hades: { label: 'Hades', accent: '#d45030' },
  gow: { label: 'God of War', accent: '#c8a040' },
  odyssey: { label: 'Odyssey', accent: '#c8a84e' },
  rome: { label: 'Rome', accent: '#c41e1e' },
  spacex: { label: 'SpaceX', accent: '#f0f0fa' },
  coinbase: { label: 'Coinbase', accent: '#0052ff' },
  diablo: { label: 'Diablo IV', accent: '#d48c28' },
  campfire: { label: 'Campfire', accent: '#c06030' },
} as const

export type ThemeStyle = keyof typeof THEME_STYLES

export const config = createTamagui({
  ...defaultConfig,
  themes: {
    ...defaultConfig.themes,
    dark: torchDark,
    light: torchLight,
    dark_odyssey: odysseyDark,
    dark_rome: romeDark,
    dark_spacex: spacexDark,
    dark_coinbase: coinbaseDark,
    dark_hades: hadesDark,
    dark_gow: gowDark,
    dark_diablo: diabloDark,
    dark_campfire: campfireDark,
  },
  fonts: {
    ...defaultConfig.fonts,
    body: spaceGroteskFont,
    heading: spaceGroteskFont,
  },
  radius,
})

export default config

export type AppConfig = typeof config

declare module 'tamagui' {
  interface TamaguiCustomConfig extends AppConfig {}
}
